import React, { useState } from 'react';
import { BookPreviewChapter, BookPreviewSection } from './BookPreview';

interface BookPreviewNavigationProps {
  chapters: BookPreviewChapter[];
  currentChapterIndex: number;
  currentSectionIndex: number;
  onChapterSelect: (chapterIndex: number) => void;
  onSectionSelect: (sectionIndex: number) => void;
  isCollapsed?: boolean;
}

export const BookPreviewNavigation: React.FC<BookPreviewNavigationProps> = ({
  chapters,
  currentChapterIndex,
  currentSectionIndex,
  onChapterSelect,
  onSectionSelect,
  isCollapsed = false
}) => {
  const [expandedChapters, setExpandedChapters] = useState<Set<number>>(new Set([currentChapterIndex]));

  const toggleChapter = (chapterIndex: number) => {
    const newExpanded = new Set(expandedChapters);
    if (newExpanded.has(chapterIndex)) {
      newExpanded.delete(chapterIndex);
    } else {
      newExpanded.add(chapterIndex);
    }
    setExpandedChapters(newExpanded);
  };

  const getSectionIcon = (type: string) => {
    switch (type) {
      case 'image':
        return '🖼️';
      case 'video':
        return '🎥';
      case 'pdf':
        return '📄';
      case 'audio':
        return '🎵';
      case 'quiz':
        return '❓';
      case 'interactive':
        return '🎮';
      case 'text':
        return '📝';
      case 'content':
        return '📚';
      case 'file':
        return '📁';
      default:
        return '📄';
    }
  };

  const getSectionTypeText = (type: string) => {
    switch (type) {
      case 'image':
        return 'Hình ảnh';
      case 'video':
        return 'Video';
      case 'pdf':
        return 'PDF';
      case 'audio':
        return 'Âm thanh';
      case 'quiz':
        return 'Kiểm tra';
      case 'interactive':
        return 'Tương tác';
      case 'text':
        return 'Văn bản';
      case 'content':
        return 'Nội dung';
      case 'file':
        return 'Tệp';
      default:
        return 'Nội dung';
    }
  };

  const getTotalDuration = (sections: BookPreviewSection[]) => {
    return sections.reduce((total, section) => total + (section.duration || 0), 0);
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} phút`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  if (isCollapsed) {
    return (
      <div className="book-preview-navigation-collapsed w-16 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
        <div className="p-2">
          <div className="text-xs text-gray-500 dark:text-gray-400 text-center mb-2">
            {chapters.length} chương
          </div>
          <div className="space-y-1">
            {chapters.map((chapter, index) => (
              <button
                key={chapter.id}
                onClick={() => onChapterSelect(index)}
                className={`w-full p-2 rounded text-xs font-medium transition-colors ${
                  index === currentChapterIndex
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
                title={chapter.title}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="book-preview-navigation w-80 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
      <div className="p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Mục lục
        </h3>
        
        <div className="space-y-2">
          {chapters.map((chapter, chapterIndex) => {
            const isExpanded = expandedChapters.has(chapterIndex);
            const isCurrentChapter = chapterIndex === currentChapterIndex;
            const totalDuration = getTotalDuration(chapter.children);

            return (
              <div key={chapter.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                {/* Chapter Header */}
                <button
                  onClick={() => toggleChapter(chapterIndex)}
                  className={`w-full p-3 text-left transition-colors ${
                    isCurrentChapter
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
                      : 'bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                      <div className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-xs font-medium text-blue-600 dark:text-blue-400">
                        {chapterIndex + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 dark:text-gray-100 truncate">
                          {chapter.title}
                        </h4>
                        <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                          <span>{chapter.children.length} phần</span>
                          {totalDuration > 0 && (
                            <>
                              <span>•</span>
                              <span>{formatDuration(totalDuration)}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform ${
                        isExpanded ? 'rotate-90' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>

                {/* Chapter Sections */}
                {isExpanded && (
                  <div className="bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                    {chapter.children.map((section, sectionIndex) => {
                      const isCurrentSection = chapterIndex === currentChapterIndex && sectionIndex === currentSectionIndex;
                      
                      return (
                        <button
                          key={section.id}
                          onClick={() => {
                            onChapterSelect(chapterIndex);
                            onSectionSelect(sectionIndex);
                          }}
                          className={`w-full p-3 text-left transition-colors border-b border-gray-200 dark:border-gray-700 last:border-b-0 ${
                            isCurrentSection
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0 text-lg">
                              {getSectionIcon(section.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <h5 className="font-medium truncate">
                                  {section.title}
                                </h5>
                                {isCurrentSection && (
                                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                )}
                              </div>
                              <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400 mt-1">
                                <span>{getSectionTypeText(section.type)}</span>
                                {section.duration && (
                                  <>
                                    <span>•</span>
                                    <span>{formatDuration(section.duration)}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="mt-6 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <div className="flex justify-between">
              <span>Tổng số chương:</span>
              <span className="font-medium">{chapters.length}</span>
            </div>
            <div className="flex justify-between">
              <span>Tổng số phần:</span>
              <span className="font-medium">
                {chapters.reduce((total, chapter) => total + chapter.children.length, 0)}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Tổng thời lượng:</span>
              <span className="font-medium">
                {formatDuration(
                  chapters.reduce((total, chapter) => total + getTotalDuration(chapter.children), 0)
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
